import React, { useEffect, useState } from 'react';
import {
    Modal, Box, Typography, Grid, Button, IconButton, TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useResponsive from '../hooks/useResponsive';
/* eslint-disable react-hooks/exhaustive-deps */

const cityStateMap = {
    Mumbai: 'Maharashtra',
    Bengaluru: 'Karnataka',
    Chennai: 'Tamil Nadu',
    Delhi: 'Delhi',
    Ahmedabad: 'Gujarat',
    Hyderabad: 'Telangana',
    Jaipur: 'Rajasthan',
    Bhopal: 'Madhya Pradesh',
    Patna: 'Bihar',
    Lucknow: 'Uttar Pradesh',
    Kolkata: 'West Bengal',
    Bhubaneswar: 'Odisha',
    Chandigarh: 'Punjab & Haryana',
    Dehradun: 'Uttarakhand',
    Raipur: 'Chhattisgarh',
    Ranchi: 'Jharkhand',
    Thiruvananthapuram: 'Kerala',
    Panaji: 'Goa',
    Gandhinagar: 'Gujarat',
    Dispur: 'Assam',
    Itanagar: 'Arunachal Pradesh',
    Imphal: 'Manipur',
    Aizawl: 'Mizoram',
    Agartala: 'Tripura',
    Shillong: 'Meghalaya',
    Kohima: 'Nagaland',
    Gangtok: 'Sikkim',
    Amaravati: 'Andhra Pradesh',
};

const stateOptions = [...new Set(Object.values(cityStateMap))];


const initialErrors = {
    firstName: '', lastName: '', houseNo: '', street: '', area: '',
    city: '', state: '', pincode: '', phoneNumber: '', email: '',
    fullName: '', parentName: '', village: '', mandal: '', landmark: '', district: '', country: ''
};

const FeildModel = ({
    open,
    handleClose,
    fields,
    addressForm,
    handleAddressChange,
    handleAddressSubmit,
    addressError,
    addressSuccess,
    setAddressForm,
}) => {
    const { isSmallScreen } = useResponsive();
    const [errors, setErrors] = useState(initialErrors);

    useEffect(() => {
        // Reset errors when modal opens
        if (open) {
            setErrors(initialErrors);
        }
        
        const selectedCity = addressForm.city;
        if (selectedCity && cityStateMap[selectedCity]) {
            setAddressForm((prev) => ({
                ...prev,
                state: cityStateMap[selectedCity],
            }));
        }
    }, [open, addressForm.city, setAddressForm]);

    const validateField = (name, value) => {
        switch (name) {
            case 'firstName':
            case 'lastName':
            case 'houseNo':
            case 'street':
            case 'area':
                if (!value.trim()) return 'Required';
                break;
            case 'city':
                if (!value) return 'Required';
                // Remove strict city validation to allow any city name
                break;
            case 'state':
                if (!value) return 'Required';
                if (!stateOptions.includes(value)) return 'Invalid state';
                break;
            case 'pincode':
                if (!/^\d{6}$/.test(value)) return 'PIN code must be 6 digits';
                break;
            case 'phoneNumber':
                if (!/^\d{10}$/.test(value)) return 'Mobile must be 10 digits';
                break;
            case 'email':
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Invalid email';
                break;
            default:
                break;
        }
        return '';
    };

    const validateAll = () => {
        const newErrors = { ...initialErrors }; // Start with clean errors
        let isValid = true;
        
        fields.forEach((field) => {
            const error = validateField(field.name, addressForm[field.name] || '');
            if (error) {
                newErrors[field.name] = error;
                isValid = false;
            } else {
                newErrors[field.name] = ''; // Explicitly set to empty string if no error
            }
        });
        
        setErrors(newErrors);
        return isValid;
    };

    const onSubmit = (e) => {
        e.preventDefault();
        if (validateAll()) {
            handleAddressSubmit(e);
        }
    };

    const handleFieldChange = (e) => {
        handleAddressChange(e);
        setErrors((prev) => ({
            ...prev,
            [e.target.name]: validateField(e.target.name, e.target.value),
        }));
    };

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: isSmallScreen ? '95vw' : 600,
        bgcolor: '#fff',
        borderRadius: 3,
        boxShadow: 24,
        p: isSmallScreen ? 2 : 4,
        maxHeight: '95vh',
        overflowY: 'auto',
    };

   // Remove auto-fill of first/last name from cookies for add-new address
   // Only edit modals may pre-fill name, not add-new


    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={style} component="form" onSubmit={onSubmit}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5" fontWeight={600} sx={{ fontFamily: 'Poppins' }}>
                        Add/Edit Address
                    </Typography>
                    <IconButton disableFocusRipple disableRipple onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Grid container spacing={2}>
                    {fields.map((field) => (
                        <Grid size={{ xs: 12, sm: 6 }} key={field.name}>
                            <TextField
                                label={field.label}
                                name={field.name}
                                type={field.name === 'email' ? 'email' : 'text'}
                                value={addressForm[field.name]}
                                onChange={handleFieldChange}
                                fullWidth
                                size="small"
                                variant="outlined"
                                inputProps={{
                                    inputMode: (field.name === 'pincode' || field.name === 'phoneNumber') ? 'numeric' : 'text',
                                    pattern:
                                        field.name === 'pincode' ? '[0-9]{6}' :
                                        field.name === 'phoneNumber' ? '[0-9]{10}' : undefined,
                                }}
                                error={!!errors[field.name]}
                                helperText={errors[field.name]}
                                sx={{
                                    fontFamily: 'Poppins',
                                    background: '#fafafa'
                                }}
                            />
                        </Grid>
                    ))}
                </Grid>

                {addressError && (
                    <Typography sx={{ color: 'red', mt: 2, fontFamily: 'Poppins' }}>
                        {addressError}
                    </Typography>
                )}

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                    <Button
                        variant="contained"
                        type="submit"
                        sx={{
                            background: '#CB4422',
                            color: '#fff',
                            fontFamily: 'Poppins',
                            fontWeight: 600,
                            fontSize: 12,
                            borderRadius: 2,
                            py: 1.2,
                            boxShadow: 'none',
                            
                        }}
                    >
                        Save Address
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default FeildModel;
